import { useState, useMemo, useCallback, memo } from "react";
import { format, addDays, startOfWeek, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay, isToday, isBefore } from "date-fns";
import { es } from "date-fns/locale";
import { ChevronLeft, ChevronRight, Calendar, Clock, Check, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useIsMobile } from "@/hooks/use-mobile";
import { getBookingTheme, BookingTheme } from "@/config/bookingThemes";

interface AvailabilitySlot {
  id: string;
  day_of_week: number;
  start_time: string;
  end_time: string;
  is_active: boolean;
}

interface BlockedDate {
  blocked_date: string;
  reason?: string | null;
}

interface AvailabilityCalendarProps {
  availability: AvailabilitySlot[];
  blockedDates: BlockedDate[];
  bookedSlots?: { date: string; start_time: string; end_time: string }[];
  onSelectSlot?: (date: Date, startTime: string, endTime: string) => void;
  isEditable?: boolean;
  theme?: BookingTheme;
  className?: string;
}

const DAY_NAMES_SHORT = ["D", "L", "M", "X", "J", "V", "S"];
const DAY_NAMES_MOBILE = ["Dom", "Lun", "Mar", "Mié", "Jue", "Vie", "Sáb"];

// Premium time slot button
const TimeSlotButton = memo(({ 
  slot, 
  status, 
  onClick,
  theme,
  compact = false 
}: { 
  slot: AvailabilitySlot;
  status: string;
  onClick: () => void;
  theme: BookingTheme;
  compact?: boolean;
}) => (
  <button
    onClick={onClick}
    disabled={status !== "available"}
    className={cn(
      "w-full rounded-lg transition-all duration-200 text-left touch-manipulation",
      compact ? "p-1.5 text-xs" : "p-2.5 sm:p-3 text-sm",
      status === "available" && [
        "bg-gradient-to-r from-primary/5 to-primary/10",
        "hover:from-primary/10 hover:to-primary/20",
        "border border-primary/20 hover:border-primary/40",
        "hover:shadow-md hover:shadow-primary/10",
        "cursor-pointer group"
      ],
      status === "booked" && "bg-muted/50 text-muted-foreground cursor-not-allowed border border-muted",
      status === "past" && "bg-muted/30 text-muted-foreground/50 cursor-not-allowed"
    )}
  >
    <div className="flex items-center gap-1.5">
      <div className={cn(
        "rounded-md flex items-center justify-center flex-shrink-0 transition-colors",
        compact ? "w-5 h-5" : "w-7 h-7 sm:w-8 sm:h-8",
        status === "available" 
          ? `bg-gradient-to-br ${theme.gradient} text-white group-hover:scale-105 transition-transform`
          : "bg-muted text-muted-foreground"
      )}>
        <Clock className={cn(compact ? "h-3 w-3" : "h-4 w-4")} />
      </div>
      <span className={cn(
        "truncate font-medium",
        status === "available" && "text-foreground"
      )}>
        {slot.start_time.slice(0, 5)}
      </span>
      {status === "available" && !compact && (
        <ChevronRight className="h-4 w-4 ml-auto text-primary opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all" />
      )}
    </div>
  </button>
));
TimeSlotButton.displayName = "TimeSlotButton";

// Premium day cell for week view
const WeekDayCell = memo(({ 
  day, 
  slots, 
  isBlocked, 
  isPast,
  getSlotStatus,
  onSlotClick,
  theme,
}: {
  day: Date;
  slots: AvailabilitySlot[];
  isBlocked: boolean;
  isPast: boolean;
  getSlotStatus: (day: Date, slot: AvailabilitySlot) => string;
  onSlotClick: (day: Date, slot: AvailabilitySlot) => void;
  theme: BookingTheme;
}) => (
  <div className={cn(
    "p-1 sm:p-1.5 border-r last:border-r-0 space-y-1 min-h-[100px] sm:min-h-[140px]",
    (isBlocked || isPast) && "bg-muted/30"
  )}>
    {isBlocked ? (
      <div className="text-xs text-muted-foreground text-center py-2">
        <div className="w-6 h-6 rounded-full bg-destructive/10 flex items-center justify-center mx-auto mb-1">
          <X className="h-3 w-3 text-destructive" />
        </div>
        <span className="hidden sm:inline">No disponible</span>
      </div>
    ) : isPast ? (
      <div className="text-xs text-muted-foreground text-center py-4 opacity-50">
        <span className="hidden sm:inline">Pasado</span>
      </div>
    ) : slots.length === 0 ? (
      <div className="text-xs text-muted-foreground text-center py-4">
        <span className="hidden sm:inline">Sin horarios</span>
        <span className="sm:hidden">-</span>
      </div>
    ) : (
      slots.map((slot) => (
        <TimeSlotButton
          key={slot.id}
          slot={slot}
          status={getSlotStatus(day, slot)}
          onClick={() => onSlotClick(day, slot)}
          theme={theme}
          compact
        />
      ))
    )}
  </div>
));
WeekDayCell.displayName = "WeekDayCell";

export const AvailabilityCalendar = memo(function AvailabilityCalendar({
  availability,
  blockedDates,
  bookedSlots = [],
  onSelectSlot,
  isEditable = false,
  theme,
  className,
}: AvailabilityCalendarProps) {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const isMobile = useIsMobile();
  const [view, setView] = useState<"week" | "month">(isMobile ? "month" : "week");
  
  const activeTheme = theme || getBookingTheme("trainer");
  const ThemeIcon = activeTheme.icon;

  // Group availability by day of week
  const availabilityByDay = useMemo(() => {
    const grouped: Record<number, AvailabilitySlot[]> = {};
    availability.filter(s => s.is_active).forEach(slot => {
      if (!grouped[slot.day_of_week]) grouped[slot.day_of_week] = [];
      grouped[slot.day_of_week].push(slot);
    });
    Object.values(grouped).forEach(slots => 
      slots.sort((a, b) => a.start_time.localeCompare(b.start_time))
    );
    return grouped;
  }, [availability]);

  // Create set of blocked dates for quick lookup
  const blockedDateSet = useMemo(() => 
    new Set(blockedDates.map(d => d.blocked_date)),
    [blockedDates]
  );

  // Create set of booked slots
  const bookedSlotSet = useMemo(() => {
    const set = new Set<string>();
    bookedSlots.forEach(slot => {
      set.add(`${slot.date}-${slot.start_time}`);
    });
    return set;
  }, [bookedSlots]);

  // Get days to display based on view
  const daysToDisplay = useMemo(() => {
    if (view === "week") {
      const weekStart = startOfWeek(currentDate, { weekStartsOn: 1 });
      return Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
    } else {
      const monthStart = startOfMonth(currentDate);
      const monthEnd = endOfMonth(currentDate);
      return eachDayOfInterval({ start: monthStart, end: monthEnd });
    }
  }, [currentDate, view]);

  const navigatePrev = useCallback(() => {
    if (view === "week") {
      setCurrentDate(prev => addDays(prev, -7));
    } else {
      setCurrentDate(prev => new Date(prev.getFullYear(), prev.getMonth() - 1, 1));
    }
  }, [view]);

  const navigateNext = useCallback(() => {
    if (view === "week") {
      setCurrentDate(prev => addDays(prev, 7));
    } else {
      setCurrentDate(prev => new Date(prev.getFullYear(), prev.getMonth() + 1, 1));
    }
  }, [view]);

  const getSlotStatus = useCallback((date: Date, slot: AvailabilitySlot) => {
    const dateStr = format(date, "yyyy-MM-dd");
    const slotKey = `${dateStr}-${slot.start_time}`;
    
    if (blockedDateSet.has(dateStr)) return "blocked";
    if (bookedSlotSet.has(slotKey)) return "booked";
    if (isBefore(date, new Date()) && !isToday(date)) return "past";
    return "available";
  }, [blockedDateSet, bookedSlotSet]);

  const handleSlotClick = useCallback((date: Date, slot: AvailabilitySlot) => {
    const status = getSlotStatus(date, slot);
    if (status === "available" && onSelectSlot) {
      onSelectSlot(date, slot.start_time, slot.end_time);
    }
  }, [getSlotStatus, onSelectSlot]);

  const formatTimeRange = useCallback((start: string, end: string) => {
    return `${start.slice(0, 5)} - ${end.slice(0, 5)}`;
  }, []);

  return (
    <Card className={cn("overflow-hidden border-2", className)}>
      {/* Premium Header */}
      <CardHeader className={cn(
        "p-3 sm:p-4 pb-2 border-b",
        `bg-gradient-to-r ${activeTheme.gradientLight}`
      )}>
        <div className="flex items-center justify-between gap-2">
          <CardTitle className="text-base sm:text-lg flex items-center gap-2">
            <div className={cn(
              "w-8 h-8 sm:w-10 sm:h-10 rounded-lg flex items-center justify-center",
              `bg-gradient-to-br ${activeTheme.gradient}`
            )}>
              <Calendar className="h-4 w-4 sm:h-5 sm:w-5 text-white" />
            </div>
            <div>
              <span className="hidden xs:inline font-semibold">Disponibilidad</span>
              <p className="text-xs text-muted-foreground font-normal hidden sm:block">Elige tu momento ideal</p>
            </div>
          </CardTitle>
          <Tabs value={view} onValueChange={(v) => setView(v as "week" | "month")}>
            <TabsList className="h-8">
              <TabsTrigger value="week" className="text-xs px-2 sm:px-3">Semana</TabsTrigger>
              <TabsTrigger value="month" className="text-xs px-2 sm:px-3">Mes</TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
        
        <div className="flex items-center justify-between mt-3">
          <Button 
            variant="outline" 
            size="icon" 
            onClick={navigatePrev} 
            className="h-8 w-8 touch-manipulation rounded-full"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="font-semibold text-sm sm:text-base text-center flex-1 capitalize">
            {view === "week" 
              ? `${format(daysToDisplay[0], "d MMM", { locale: es })} - ${format(daysToDisplay[6], "d MMM", { locale: es })}`
              : format(currentDate, "MMMM yyyy", { locale: es })
            }
          </span>
          <Button 
            variant="outline" 
            size="icon" 
            onClick={navigateNext} 
            className="h-8 w-8 touch-manipulation rounded-full"
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </CardHeader>
      
      <CardContent className="p-0">
        {/* Week View */}
        {view === "week" && (
          <div className="overflow-x-auto">
            <div className="min-w-[320px] sm:min-w-[500px]">
              {/* Header row */}
              <div className="grid grid-cols-7 border-b bg-muted/20">
                {daysToDisplay.map((day, idx) => (
                  <div 
                    key={idx}
                    className={cn(
                      "p-1.5 sm:p-2 text-center border-r last:border-r-0 transition-colors",
                      isToday(day) && `bg-gradient-to-b ${activeTheme.gradientLight}`
                    )}
                  >
                    <div className="text-[10px] sm:text-xs text-muted-foreground">
                      {isMobile ? DAY_NAMES_SHORT[day.getDay()] : DAY_NAMES_MOBILE[day.getDay()]}
                    </div>
                    <div className={cn(
                      "text-xs sm:text-sm font-semibold",
                      isToday(day) && "text-primary"
                    )}>
                      {format(day, "d")}
                    </div>
                  </div>
                ))}
              </div>
              
              {/* Slots row */}
              <div className="grid grid-cols-7">
                {daysToDisplay.map((day, idx) => {
                  const dayOfWeek = day.getDay();
                  const daySlots = availabilityByDay[dayOfWeek] || [];
                  const isBlocked = blockedDateSet.has(format(day, "yyyy-MM-dd"));
                  const isPast = isBefore(day, new Date()) && !isToday(day);
                  
                  return (
                    <WeekDayCell
                      key={idx}
                      day={day}
                      slots={daySlots}
                      isBlocked={isBlocked}
                      isPast={isPast}
                      getSlotStatus={getSlotStatus}
                      onSlotClick={handleSlotClick}
                      theme={activeTheme}
                    />
                  );
                })}
              </div>
            </div>
          </div>
        )}
        
        {/* Month View */}
        {view === "month" && (
          <div className="p-2 sm:p-3">
            {/* Day headers */}
            <div className="grid grid-cols-7 mb-1">
              {DAY_NAMES_SHORT.map((day, idx) => (
                <div key={idx} className="text-[10px] sm:text-xs text-center text-muted-foreground font-medium py-1">
                  {day}
                </div>
              ))}
            </div>
            
            {/* Calendar grid */}
            <div className="grid grid-cols-7 gap-0.5 sm:gap-1">
              {/* Empty cells for days before month start */}
              {Array.from({ length: (daysToDisplay[0].getDay() + 6) % 7 }, (_, i) => (
                <div key={`empty-${i}`} className="aspect-square" />
              ))}
              
              {daysToDisplay.map((day, idx) => {
                const dayOfWeek = day.getDay();
                const hasSlots = (availabilityByDay[dayOfWeek] || []).length > 0;
                const isBlocked = blockedDateSet.has(format(day, "yyyy-MM-dd"));
                const isPast = isBefore(day, new Date()) && !isToday(day);
                const isSelected = selectedDate && isSameDay(day, selectedDate);
                const slotsCount = (availabilityByDay[dayOfWeek] || []).length;
                
                return (
                  <button
                    key={idx}
                    onClick={() => setSelectedDate(day)}
                    disabled={!hasSlots || isBlocked || isPast}
                    className={cn(
                      "aspect-square rounded-xl text-xs sm:text-sm flex flex-col items-center justify-center transition-all duration-200 relative touch-manipulation",
                      isToday(day) && "ring-2 ring-primary ring-offset-1",
                      isSelected && `bg-gradient-to-br ${activeTheme.gradient} text-white shadow-lg scale-105`,
                      hasSlots && !isBlocked && !isPast && !isSelected && [
                        "bg-primary/5 hover:bg-primary/15",
                        "hover:scale-105 hover:shadow-md"
                      ],
                      isBlocked && "bg-destructive/10 text-destructive/50",
                      isPast && "text-muted-foreground opacity-40",
                      !hasSlots && !isBlocked && !isPast && "text-muted-foreground"
                    )}
                  >
                    <span className="font-medium">{format(day, "d")}</span>
                    {/* Availability indicator dots */}
                    {hasSlots && !isBlocked && !isPast && (
                      <div className="flex gap-0.5 mt-0.5">
                        {Array.from({ length: Math.min(3, slotsCount) }).map((_, i) => (
                          <div key={i} className={cn(
                            "w-1 h-1 rounded-full",
                            isSelected ? "bg-white" : "bg-primary"
                          )} />
                        ))}
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
            
            {/* Selected date details */}
            {selectedDate && (
              <div className={cn(
                "mt-4 p-4 rounded-xl border-2 animate-slide-up-fade",
                `bg-gradient-to-r ${activeTheme.gradientLight} border-primary/20`
              )}>
                <div className="text-sm font-semibold mb-3 flex items-center gap-2">
                  <div className={cn(
                    "w-8 h-8 rounded-lg flex items-center justify-center",
                    `bg-gradient-to-br ${activeTheme.gradient}`
                  )}>
                    <Calendar className="h-4 w-4 text-white" />
                  </div>
                  {format(selectedDate, "EEEE d 'de' MMMM", { locale: es })}
                </div>
                <div className="space-y-2">
                  {(availabilityByDay[selectedDate.getDay()] || []).map((slot) => {
                    const status = getSlotStatus(selectedDate, slot);
                    return (
                      <TimeSlotButton
                        key={slot.id}
                        slot={{ ...slot, start_time: formatTimeRange(slot.start_time, slot.end_time).split(' - ')[0], end_time: slot.end_time }}
                        status={status}
                        onClick={() => handleSlotClick(selectedDate, slot)}
                        theme={activeTheme}
                      />
                    );
                  })}
                  {(availabilityByDay[selectedDate.getDay()] || []).length === 0 && (
                    <p className="text-sm text-muted-foreground text-center py-4">
                      No hay horarios disponibles
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
        
        {/* Legend */}
        <div className="flex flex-wrap gap-3 sm:gap-4 p-3 sm:p-4 border-t bg-muted/10">
          <div className="flex items-center gap-1.5">
            <div className={cn(
              "w-3 h-3 rounded-full",
              `bg-gradient-to-br ${activeTheme.gradient}`
            )} />
            <span className="text-xs text-muted-foreground">Disponible</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded-full bg-muted border" />
            <span className="text-xs text-muted-foreground">Reservado</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
});
